import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';
import { pContractDocEditorActions } from './slice';
import { PayloadAction } from '@reduxjs/toolkit';
import { manuallyDecrementPromiseCounter, manuallyIncrementPromiseCounter, trackPromise } from 'react-promise-tracker';
import { httpClientError, httpSuccess } from 'app/services/serviceHelpers';
import { PlacementService } from 'app/services/PlacementServices/placement-service';
import type { IContractTemplateTemplateRowsResponse } from 'app/services/PlacementServices/placement-service';
import { TemplateService } from 'app/services/TemplateService/template-service';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { deriveTokensObj } from '../contract-template-editor/helper';
import { IContractTemplate } from '../contract-template-editor/types';
import {
  findContractDocMatchingDraftResponse,
  formatTemplates,
  getPlacementCandidateContractAddSnack,
  mergeContractDocWithDraftResponse,
  tryGetPlacementCandidateContractApiErrorEnvelopeMessage,
} from './helper';
import { selectUser } from 'oidc/user.selectors';
import { IUser } from 'oidc/user.redux';
import { Concatenate } from 'utils/string/string';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import queryString from 'query-string';
import i18next from 'i18next';
import {
  IContractDocCreatePayload,
  IContractDocDraftDeleteNewPayload,
  IContractDocPublishPayload,
  IContractDocSaveDraftPayload,
  IContractDocStatusUpdatePayload,
  IContractDocTscPublishPayload,
  IContractDocWritePayload,
} from './async-actions';
import { placementContractHtmlToRtf } from 'app/helpers/placementContractRtfRepair';
import { SharedService } from 'app/services/SharedServices/shared-service';
import { downloadSelectedCandidateContract, onContractDocClick, onInit } from './business.saga';
import { selectDocumentView } from './selector';
import {
  ContractDocSource,
  ContractStatus,
  IContractDoc,
  isAmieWebContractHtmlEditorByStatus,
  isPlacementContractPdfByStatus,
  isValidPlacementContractDocumentImageGuid,
  opensInCandidateContractHtmlEditor,
  resolvePlacementContractDocumentTypeId,
} from './types';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';
import { SnackQueueProvider } from 'store/configureStore';

const TrackMergeTokensWrapper = (fn, ...args) => trackPromise(fn(...args), PromiseTrackerKeys.placements.mergeTokens);
const TrackGenerateDraftWrapper = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placements.generateDraft);
const TrackAddCandidateContractWrapper = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placements.createDocument);
const TrackDeleteDraftWrapper = (fn, ...args) => trackPromise(fn(...args), PromiseTrackerKeys.placements.deleteDraft);
const TrackPreviewWrapper = (fn, ...args) => trackPromise(fn(...args), PromiseTrackerKeys.placements.previewDocument);
const TrackDraftSaveWrapper = (fn, ...args) => trackPromise(fn(...args), PromiseTrackerKeys.placements.saveDraft);
const TrackRequestPdfDoc = (fn, ...args) => trackPromise(fn(...args), PromiseTrackerKeys.placements.requestPdfDoc);
const TrackContractCatalogWrapper = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placements.contractCatalogTemplates);
const TrackTemplateRowsWrapper = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placements.contractTemplateRows);

function extractContractCatalogPayload(data: unknown): IContractTemplate[] {
  if (data == null) return [];
  if (Array.isArray(data)) return data as IContractTemplate[];
  const o = data as Record<string, unknown>;
  const nested = o.data ?? o.items ?? o.templates ?? o.result;
  if (Array.isArray(nested)) return nested as IContractTemplate[];
  return [];
}

export function* requestContractCatalogTemplates() {
  try {
    const templateService: TemplateService = yield getContext('templateService');
    const response = yield call(TrackContractCatalogWrapper, templateService.getTemplatesNew, true, 'Contract');
    if (httpSuccess(response?.status)) {
      yield put(pContractDocEditorActions.setCatalogTemplates(extractContractCatalogPayload(response.data)));
    } else {
      yield put(pContractDocEditorActions.setCatalogTemplates([]));
    }
  } catch (error) {
    yield put(pContractDocEditorActions.setCatalogTemplates([]));
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'requestContractCatalogTemplates',
        area: 'placement-candidate-contract-doc-editor/saga',
      },
    });
  }
}

export function* requestContractTemplateRows(
  action: PayloadAction<{ placementId: number; contractId: number; templateId: number }>,
) {
  try {
    const placementService: PlacementService = yield getContext('placementService');
    const response = yield call(
      TrackTemplateRowsWrapper,
      placementService.postContractTemplateTemplateRows,
      action.payload,
    );
    if (httpSuccess(response?.status)) {
      const data = response.data as IContractTemplateTemplateRowsResponse;
      const html = data?.templateHtml?.trim() ? data.templateHtml : '';
      if (html) {
        yield put(pContractDocEditorActions.setPendingTinyMceHtmlInsert(html));
      } else {
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('templateEditor.placement.snackbar.contractEditor.templateRowsNoHtml'),
            severity: 'warning',
          }),
        );
      }
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.templateRowsFailure'),
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'requestContractTemplateRows',
        area: 'placement-candidate-contract-doc-editor/saga',
      },
    });
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('templateEditor.placement.snackbar.contractEditor.templateRowsFailure'),
        severity: 'error',
      }),
    );
  }
}

function* fetchContractPdfAfterTemplateUpdate(updatedDoc: IContractDoc | null | undefined) {
  if (!updatedDoc) return null;
  const sharedService: SharedService = yield getContext('sharedService');
  const idStr = resolvePlacementContractDocumentTypeId(updatedDoc);
  if (
    (updatedDoc.source === ContractDocSource.amieClassic || isPlacementContractPdfByStatus(updatedDoc)) &&
    idStr
  ) {
    const response = yield call(
      TrackRequestPdfDoc,
      sharedService.getPlacementClassicDocument,
      'Contract',
      parseInt(idStr, 10),
    );
    if (httpSuccess(response?.status))
      return {
        ...response.data,
        data: response.data.data,
        documentId: response.data.id ?? idStr,
      };
  }
  if (isValidPlacementContractDocumentImageGuid(updatedDoc.documentImageId)) {
    const response = yield call(
      TrackRequestPdfDoc,
      sharedService.getPlacementDocument,
      String(updatedDoc.documentImageId).trim(),
    );
    if (httpSuccess(response?.status))
      return {
        ...response.data,
        data: response.data.filebytes,
        documentId: response.data.documentId ?? updatedDoc.documentId,
      };
  }
  return null;
}

export function* requestTemplates(action: PayloadAction<{ placementId: number }>) {
  try {
    manuallyIncrementPromiseCounter('placement-candidate-contract-templates');
    const placementService: PlacementService = yield getContext('placementService');

    const [templatesResponse] = yield all([call(placementService.getPlacementContracts, action.payload.placementId)]);
    if (httpSuccess(templatesResponse.status)) {
      const formattedTemplates = formatTemplates(
        templatesResponse?.data?.placementContractDetailsItems,
        action.payload.placementId,
      );
      yield put(pContractDocEditorActions.setTemplates(formattedTemplates));
      yield put(
        pContractDocEditorActions.setDocStatusExists({
          amieWebDraftExists: formattedTemplates.some(
            x =>
              x.status === ContractStatus.draft ||
              (x.source === ContractDocSource.amieWeb && x.status === ContractStatus.pending),
          ),
          amieWebCreatedExists: formattedTemplates.some(
            x => x.status === ContractStatus.created && x.source === ContractDocSource.amieWeb,
          ),
          classicDraftExists: templatesResponse?.data?.isClassicDraftPresent,
        }),
      );
      return formattedTemplates;
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'requestTemplates',
        area: 'src/store/redux-store/placement-client-confirmation/saga',
      },
    });
  } finally {
    manuallyDecrementPromiseCounter('placement-candidate-contract-templates');
  }
}

export function* requestMergeTokenList() {
  try {
    const templateService: TemplateService = yield getContext('templateService');
    const response = yield call(TrackMergeTokensWrapper, templateService.getMergeTokensList, 'Contract');
    if (httpSuccess(response.status)) {
      const tokenObj = deriveTokensObj(response.data);
      yield put(pContractDocEditorActions.setMergeTokenList(tokenObj));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'requestMergeTokenList',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* generateDraft(action: PayloadAction<{ placementId: number }>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');
    const {
      userInfo: { employeeId: userId, firstName, lastName },
    }: IUser = yield select(selectUser);
    const { renderPdfView, renderPreviewScreen } = yield select(selectDocumentView);

    const response = yield call(TrackGenerateDraftWrapper, placementService.createPlacementContractTemplateDraft, {
      ...action.payload,
      userId,
      userName: Concatenate([firstName, lastName], ' '),
    });
    if (httpSuccess(response.status)) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.generateSuccess'),
          severity: 'success',
        }),
      );
      const templates = yield call(requestTemplates, {
        payload: { placementId: action.payload.placementId },
        type: pContractDocEditorActions.getTemplates.type,
      });
      const draftData = response.data;
      const generatedTemplate = findContractDocMatchingDraftResponse(templates, draftData);

      if (generatedTemplate) {
        const withDraftContent = mergeContractDocWithDraftResponse(generatedTemplate, {
          templateHtml: draftData?.templateHtml ?? draftData?.TemplateHtml,
          templateText: draftData?.templateText ?? draftData?.TemplateText,
        });
        yield call(
          onContractDocClick,
          pContractDocEditorActions.business_OnContractDocClick({ contract: withDraftContent }),
        );
      } else {
        yield put(pContractDocEditorActions.setSelectedTemplate(null));
        if (renderPdfView || renderPreviewScreen) yield put(pContractDocEditorActions.resetToDocView());

        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.has('documentId')) {
          urlParams.delete('documentId');
          urlParams.delete('documentImageId');
          urlParams.delete('source');
          window.history.pushState(null, null, `${window.location.pathname}?${urlParams.toString()}`);
        }
      }
    } else
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.generateFailure'),
          severity: 'error',
        }),
      );
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'generateDraft',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* deleteContractDraft(action: PayloadAction<{ placementId: number }>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');
    const {
      userInfo: { employeeId: userId, firstName, lastName },
    }: IUser = yield select(selectUser);

    const response = yield call(TrackDeleteDraftWrapper, placementService.deletePlacementContractDraft, {
      ...action.payload,
      userId,
      userName: Concatenate([firstName, lastName], ' '),
    });
    if (httpSuccess(response.status)) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.deleteDraftSuccess'),
          severity: 'success',
        }),
      );
      yield call(requestTemplates, {
        payload: { placementId: action.payload.placementId },
        type: pContractDocEditorActions.getTemplates.type,
      });
      /** Code: Set selected template as null on deletion */
      yield put(pContractDocEditorActions.setSelectedTemplate(null));
    } else
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.deleteDraftFailure'),
          severity: 'error',
        }),
      );
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'deleteContractDraft',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* deleteContractDraftNew(action: PayloadAction<IContractDocDraftDeleteNewPayload>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');
    const {
      userInfo: { employeeId: userId },
    }: IUser = yield select(selectUser);
    const { placementId, documentId } = action.payload;

    const documentIdNum =
      typeof documentId === 'number' && Number.isFinite(documentId)
        ? documentId
        : parseInt(String(documentId ?? '').trim(), 10);
    if (!Number.isFinite(documentIdNum) || documentIdNum <= 0) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.deleteDraftFailure'),
          severity: 'error',
        }),
      );
      return;
    }

    const response = yield call(TrackDeleteDraftWrapper, placementService.deletePlacementContractDraftNew, {
      placementId,
      documentId: documentIdNum,
      userId,
    });

    if (httpSuccess(response?.status) && response?.data === 0) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.deleteDraftSuccess'),
          severity: 'success',
        }),
      );
      yield call(requestTemplates, {
        payload: { placementId },
        type: pContractDocEditorActions.getTemplates.type,
      });
      yield put(pContractDocEditorActions.setSelectedTemplate(null));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.deleteDraftFailure'),
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'deleteContractDraftNew',
        area: 'placement-candidate-contract-doc-editor/saga',
      },
    });
  }
}

export function* deleteContractDoc(action: PayloadAction<{ placementId: number; contractId: string }>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');
    const {
      userInfo: { employeeId: userId, firstName, lastName },
    }: IUser = yield select(selectUser);

    const response = yield call(TrackDeleteDraftWrapper, placementService.deletePlacementContract, {
      ...action.payload,
      userId,
      userName: Concatenate([firstName, lastName], ' '),
    });
    if (httpSuccess(response.status)) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.deleteCandidateContractSuccess'),
          severity: 'success',
        }),
      );

      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete('documentId');
      urlParams.delete('source');
      window.history.pushState(null, null, `${window.location.pathname}?${urlParams.toString()}`);

      yield call(requestTemplates, {
        payload: { placementId: action.payload.placementId },
        type: pContractDocEditorActions.getTemplates.type,
      });
      /** Code: Set selected template as null on deletion */
      yield put(pContractDocEditorActions.setSelectedTemplate(null));
      yield put(pContractDocEditorActions.resetToDocView());
    } else
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.deleteCandidateContractFailure'),
          severity: 'error',
        }),
      );
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'deleteContractDoc',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* saveDraftTemplate(action: PayloadAction<IContractDocSaveDraftPayload>) {
  try {
    const { placementId, documentId, templateHtml, preservedApiTemplateTextRtf } = action.payload;
    if (!Number.isFinite(documentId) || documentId <= 0) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.saveDraftFailure'),
          severity: 'error',
        }),
      );
      return;
    }
    const placementService: PlacementService = yield getContext('placementService');
    const {
      userInfo: { employeeId: userId, firstName, lastName },
    }: IUser = yield select(selectUser);
    const response = yield call(TrackDraftSaveWrapper, placementService.savePlacementContractDraft, {
      placementId,
      documentId,
      templateText: placementContractHtmlToRtf(templateHtml, preservedApiTemplateTextRtf),
      templateHtml,
      userId,
      userName: Concatenate([firstName, lastName], ' '),
    });
    if (httpSuccess(response.status)) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.saveDraftSuccess'),
          severity: 'success',
        }),
      );
      const templates = yield call(requestTemplates, {
        payload: { placementId },
        type: pContractDocEditorActions.getTemplates.type,
      });
      const newDraftTemplate =
        templates.find(x => x.id === response.data?.id) ??
        templates.find(x => String(x.documentId) === String(documentId));
      yield put(pContractDocEditorActions.setSelectedTemplate(newDraftTemplate ?? null));
    } else
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.saveDraftFailure'),
          severity: 'error',
        }),
      );
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'saveDraftTemplate',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* updateContractStatusSaga(action: PayloadAction<IContractDocStatusUpdatePayload>) {
  try {
    manuallyIncrementPromiseCounter(PromiseTrackerKeys.placements.contractStatusUpdate);
    const {
      userInfo: { employeeId: userId },
    }: IUser = yield select(selectUser);
    const placementService: PlacementService = yield getContext('placementService');
    const { placementId, documentId, statusId } = action.payload;

    const documentIdNum =
      typeof documentId === 'number' && Number.isFinite(documentId)
        ? documentId
        : parseInt(String(documentId).trim(), 10);
    if (!Number.isFinite(documentIdNum) || documentIdNum <= 0) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.statusUpdateFailure'),
          severity: 'error',
        }),
      );
      return;
    }

    const response = yield call(placementService.updatePlacementCandidateContractStatus, {
      placementId,
      documentId: documentIdNum,
      statusId,
      userId,
    });

    if (httpSuccess(response.status)) {
      const data = response.data as { documentId?: number; DocumentId?: number } | undefined;
      const respDocumentId = data?.documentId ?? data?.DocumentId;
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.statusUpdateSuccess'),
          severity: 'success',
        }),
      );
      const templates = yield call(requestTemplates, {
        payload: { placementId },
        type: pContractDocEditorActions.getTemplates.type,
      });
      if (templates?.length) {
        const matchDocId = respDocumentId != null ? String(respDocumentId) : String(documentIdNum);
        const updatedContract = templates.find(x => String(x.documentId) === matchDocId);
        if (updatedContract) {
          yield call(
            onContractDocClick,
            pContractDocEditorActions.business_OnContractDocClick({ contract: updatedContract }),
          );
        } else {
          yield put(pContractDocEditorActions.setSelectedTemplate(null));
        }
      }
    } else {
      const errData = response?.data as { errorMessage?: string } | undefined;
      const message =
        errData && typeof errData === 'object' && errData.errorMessage
          ? String(errData.errorMessage)
          : i18next.t('templateEditor.placement.snackbar.contractEditor.statusUpdateFailure');
      yield put(globalActions.setSnackBar({ message, severity: 'error' }));
    }
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('templateEditor.placement.snackbar.contractEditor.statusUpdateFailure'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'updateContractStatusSaga',
        area: 'src/store/redux-store/placement-candidate-contract-doc-editor/saga',
      },
    });
  } finally {
    manuallyDecrementPromiseCounter(PromiseTrackerKeys.placements.contractStatusUpdate);
  }
}

export function* previewContractDoc(action: PayloadAction<IContractDocWritePayload>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');

    const response = yield call(TrackPreviewWrapper, placementService.previewContractDoc, action.payload);
    if (httpSuccess(response.status)) {
      yield put(
        pContractDocEditorActions.setPreviewScreen({
          data: response.data.documentHtmlContent,
          version: response.data.version,
        }),
      );
    } else
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.previewFailure'),
          severity: 'error',
        }),
      );
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('templateEditor.placement.snackbar.contractEditor.previewFailure'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'previewContractDoc',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* createContractDoc(action: PayloadAction<IContractDocCreatePayload>) {
  try {
    manuallyIncrementPromiseCounter(PromiseTrackerKeys.placements.createDocument);
    const { placementId, documentId, templateHtml, preservedApiTemplateTextRtf } = action.payload;
    const docIdInvalid =
      documentId == null ||
      (typeof documentId === 'string' && documentId.trim() === '') ||
      (typeof documentId === 'number' && (!Number.isFinite(documentId) || documentId <= 0));
    if (docIdInvalid) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.createFailure'),
          severity: 'error',
        }),
      );
      return;
    }
    const {
      userInfo: { employeeId: userId, firstName, lastName },
    }: IUser = yield select(selectUser);
    const placementService: PlacementService = yield getContext('placementService');
    const { renderPdfView, renderPreviewScreen } = yield select(selectDocumentView);

    const response = yield call(
      TrackAddCandidateContractWrapper,
      placementService.addPlacementCandidateContract,
      {
        placementId,
        documentId,
        templateText: placementContractHtmlToRtf(templateHtml, preservedApiTemplateTextRtf),
        templateHtml,
        userId,
        userName: Concatenate([firstName, lastName], ' '),
      },
    );
    if (httpSuccess(response.status)) {
      const { message, severity } = getPlacementCandidateContractAddSnack(response.data);
      yield put(globalActions.setSnackBar({ message, severity }));
      const templates = yield call(requestTemplates, {
        payload: { placementId },
        type: pContractDocEditorActions.getTemplates.type,
      });
      const draftData = response.data;
      /** Match side panel row using `documentId` from the add request body (draft id), not add response ids. */
      let generatedTemplate = findContractDocMatchingDraftResponse(templates, {
        documentId,
      });
      if (!generatedTemplate) {
        generatedTemplate = findContractDocMatchingDraftResponse(templates, draftData);
      }

      if (generatedTemplate) {
        const withDraftContent = mergeContractDocWithDraftResponse(generatedTemplate, {
          templateHtml: draftData?.templateHtml ?? draftData?.TemplateHtml,
          templateText: draftData?.templateText ?? draftData?.TemplateText,
        });
        yield call(
          onContractDocClick,
          pContractDocEditorActions.business_OnContractDocClick({ contract: withDraftContent }),
        );
      } else {
        yield put(pContractDocEditorActions.setSelectedTemplate(null));
        if (renderPdfView || renderPreviewScreen) yield put(pContractDocEditorActions.resetToDocView());

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('documentId')) {
          urlParams.delete('documentId');
          urlParams.delete('documentImageId');
          urlParams.delete('source');
          window.history.pushState(null, null, `${window.location.pathname}?${urlParams.toString()}`);
        }
      }
    } else {
      const apiMessage = tryGetPlacementCandidateContractApiErrorEnvelopeMessage(response?.data);
      yield put(
        globalActions.setSnackBar({
          message:
            apiMessage ?? i18next.t('templateEditor.placement.snackbar.contractEditor.createFailure'),
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('templateEditor.placement.snackbar.contractEditor.createFailure'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'createContractDoc',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  } finally {
    manuallyDecrementPromiseCounter(PromiseTrackerKeys.placements.createDocument);
  }
}

export function* publishContractDoc(action: PayloadAction<IContractDocPublishPayload>) {
  try {
    manuallyIncrementPromiseCounter(PromiseTrackerKeys.placements.publishDocument);
    const {
      userInfo: { employeeId: userId, firstName, lastName },
    } = yield select(selectUser);
    const placementService: PlacementService = yield getContext('placementService');
    const sharedService: SharedService = yield getContext('sharedService');

    const response = yield call(placementService.publishPlacementContract, {
      ...action.payload,
      actionType: action.payload.actionType ?? 'Publish',
      userId,
      userName: Concatenate([firstName, lastName], ' '),
    });
    if (httpSuccess(response.status)) {
      if (response.data.some(x => x.messageId === 100014))
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('templateEditor.placement.snackbar.contractEditor.publish.100014'),
            severity: 'success',
          }),
        );
      else if (response.data.some(x => x.messageId === 100019))
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('templateEditor.placement.snackbar.contractEditor.publish.100019'),
            severity: 'success',
          }),
        );
      else if (response.data.some(x => x.messageId === 100016)) {
        globalActions.setStackSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.publish.success_100016'),
          severity: 'success',
        });
        globalActions.setStackSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.publish.failure_100016'),
          severity: 'error',
        });
      }

      const templates = yield call(requestTemplates, {
        payload: { placementId: action.payload.placementId },
        type: pContractDocEditorActions.getTemplates.type,
      });
      if (templates) {
        const updatedDoc = templates.find(x => x.id === action.payload.contractId);
        const documentPayload = yield call(fetchContractPdfAfterTemplateUpdate, updatedDoc ?? null);
        if (documentPayload) {
          const document = { ...documentPayload, data: documentPayload.data };
          yield put(pContractDocEditorActions.setPdfView(document));
          yield put(pContractDocEditorActions.setSelectedTemplate(updatedDoc ?? null));

          const urlParams = new URLSearchParams(window.location.search);
          const typeId = updatedDoc ? resolvePlacementContractDocumentTypeId(updatedDoc) : null;
          urlParams.set('documentId', typeId ?? String(document.documentId));
          urlParams.set('source', updatedDoc?.source ?? ContractDocSource.amieWeb);
          urlParams.delete('default_selection');
          if (updatedDoc && (isPlacementContractPdfByStatus(updatedDoc) || updatedDoc.source === ContractDocSource.amieClassic)) {
            urlParams.delete('documentImageId');
          }
          window.history.pushState(null, null, `${window.location.pathname}?${urlParams.toString()}`);
        } else {
          yield put(pContractDocEditorActions.setSelectedTemplate(null));
        }
      } else {
        yield put(pContractDocEditorActions.setSelectedTemplate(null));
      }
    } else if (response.status === 400) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.publish.100015'),
          severity: 'error',
        }),
      );
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.publish.failure'),
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('templateEditor.placement.snackbar.contractEditor.publish.failure'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'publishContractDoc',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  } finally {
    manuallyDecrementPromiseCounter(PromiseTrackerKeys.placements.publishDocument);
  }
}

export function* publishContractTscNew(action: PayloadAction<IContractDocTscPublishPayload>) {
  try {
    manuallyIncrementPromiseCounter(PromiseTrackerKeys.placements.tscPublishDocument);
    const {
      userInfo: { employeeId: userId },
    } = yield select(selectUser);
    const placementService: PlacementService = yield getContext('placementService');
    const { placementId, documentId, shouldSetTsc } = action.payload;

    const documentIdNum =
      typeof documentId === 'number' && Number.isFinite(documentId)
        ? documentId
        : parseInt(String(documentId ?? '').trim(), 10);
    if (!Number.isFinite(documentIdNum) || documentIdNum <= 0) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.statusUpdateFailure'),
          severity: 'error',
        }),
      );
      return;
    }

    const response = yield call(placementService.publishPlacementContractTscNew, {
      placementId,
      documentId: documentIdNum,
      shouldSetTsc,
      userId,
    });

    if (httpSuccess(response?.status) && response?.data === 0) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.statusUpdateSuccess'),
          severity: 'success',
        }),
      );

      const templates = yield call(requestTemplates, {
        payload: { placementId },
        type: pContractDocEditorActions.getTemplates.type,
      });
      if (templates) {
        const updatedDoc = templates.find(x => String(x.documentId) === String(documentIdNum));
        const documentPayload = yield call(fetchContractPdfAfterTemplateUpdate, updatedDoc ?? null);
        if (documentPayload) {
          const document = { ...documentPayload, data: documentPayload.data };
          yield put(pContractDocEditorActions.setPdfView(document));
          yield put(pContractDocEditorActions.setSelectedTemplate(updatedDoc ?? null));

          const urlParams = new URLSearchParams(window.location.search);
          const typeId = updatedDoc ? resolvePlacementContractDocumentTypeId(updatedDoc) : null;
          urlParams.set('documentId', typeId ?? String(document.documentId));
          urlParams.set('source', updatedDoc?.source ?? ContractDocSource.amieWeb);
          urlParams.delete('default_selection');
          if (
            updatedDoc &&
            (isPlacementContractPdfByStatus(updatedDoc) || updatedDoc.source === ContractDocSource.amieClassic)
          ) {
            urlParams.delete('documentImageId');
          }
          window.history.pushState(null, null, `${window.location.pathname}?${urlParams.toString()}`);
        } else {
          yield put(pContractDocEditorActions.setSelectedTemplate(updatedDoc ?? null));
        }
      } else {
        yield put(pContractDocEditorActions.setSelectedTemplate(null));
      }
    } else if (response?.status === 400) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.statusUpdateFailure'),
          severity: 'error',
        }),
      );
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.statusUpdateFailure'),
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('templateEditor.placement.snackbar.contractEditor.statusUpdateFailure'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'publishContractTscNew',
        area: 'placement-candidate-contract-doc-editor/saga',
      },
    });
  } finally {
    manuallyDecrementPromiseCounter(PromiseTrackerKeys.placements.tscPublishDocument);
  }
}

export function* voidContractDoc(action: PayloadAction<IContractDocPublishPayload>) {
  try {
    manuallyIncrementPromiseCounter(PromiseTrackerKeys.placements.voidDocument);
    const {
      userInfo: { employeeId: userId, firstName, lastName },
    } = yield select(selectUser);
    const placementService: PlacementService = yield getContext('placementService');

    const response = yield call(placementService.publishPlacementContract, {
      ...action.payload,
      actionType: 'Void',
      userId,
      userName: Concatenate([firstName, lastName], ' '),
    });
    if (httpSuccess(response.status)) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.voidSuccess'),
          severity: 'success',
        }),
      );
      const templates = yield call(requestTemplates, {
        payload: { placementId: action.payload.placementId },
        type: pContractDocEditorActions.getTemplates.type,
      });
      if (templates) {
        const updatedDoc = templates.find(x => x.id === action.payload.contractId);
        const documentPayload = yield call(fetchContractPdfAfterTemplateUpdate, updatedDoc ?? null);
        if (documentPayload) {
          const document = { ...documentPayload, data: documentPayload.data };
          yield put(pContractDocEditorActions.setPdfView(document));
          yield put(pContractDocEditorActions.setSelectedTemplate(updatedDoc ?? null));

          const urlParams = new URLSearchParams(window.location.search);
          const typeId = updatedDoc ? resolvePlacementContractDocumentTypeId(updatedDoc) : null;
          urlParams.set('documentId', typeId ?? String(document.documentId));
          urlParams.set('source', updatedDoc?.source ?? ContractDocSource.amieWeb);
          urlParams.delete('default_selection');
          if (updatedDoc && (isPlacementContractPdfByStatus(updatedDoc) || updatedDoc.source === ContractDocSource.amieClassic)) {
            urlParams.delete('documentImageId');
          }
          window.history.pushState(null, null, `${window.location.pathname}?${urlParams.toString()}`);
        } else {
          yield put(pContractDocEditorActions.setSelectedTemplate(null));
        }
      } else {
        yield put(pContractDocEditorActions.setSelectedTemplate(null));
      }
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.voidFailure'),
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('templateEditor.placement.snackbar.contractEditor.voidFailure'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'publishVoidContractDoc',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  } finally {
    manuallyDecrementPromiseCounter(PromiseTrackerKeys.placements.voidDocument);
  }
}

/** Resolve PDF from URL after templates load (OK to Send / Sent by TCC / Classic use Contract doc-type API). */
export function* getDocumentFromUrlId(options?: { templates?: IContractDoc[] }) {
  try {
    const qs = queryString.parse(window.location.search);
    const rawDocId = qs.documentId;
    if (rawDocId == null || rawDocId === '') return;

    const documentId = Array.isArray(rawDocId) ? rawDocId[0] : rawDocId;
    if (!documentId) return;

    const rawImageId = qs.documentImageId;
    const rawSource = qs.source;
    const docImageRaw = Array.isArray(rawImageId) ? rawImageId[0] : rawImageId;
    const sourceRaw = Array.isArray(rawSource) ? rawSource[0] : rawSource;

    const source: ContractDocSource =
      sourceRaw === ContractDocSource.amieClassic || sourceRaw === 'AMIE Classic'
        ? ContractDocSource.amieClassic
        : ContractDocSource.amieWeb;

    const templates = options?.templates;
    const contractFromList = templates?.find(
      x =>
        String(x.documentId) === String(documentId) ||
        String(x.id) === String(documentId),
    );

    if (contractFromList && opensInCandidateContractHtmlEditor(contractFromList)) return;

    const docImageStr =
      docImageRaw && String(docImageRaw).trim() !== '' ? String(docImageRaw).trim() : undefined;

    const useContractDocumentTypeApi =
      source === ContractDocSource.amieClassic ||
      (!!contractFromList && (isPlacementContractPdfByStatus(contractFromList) || contractFromList.source === ContractDocSource.amieClassic)) ||
      (source === ContractDocSource.amieWeb &&
        /^\d+$/.test(String(documentId).trim()) &&
        !docImageStr &&
        !contractFromList);

    const resolvedImageId =
      docImageStr ??
      (!useContractDocumentTypeApi && contractFromList?.documentImageId
        ? String(contractFromList.documentImageId).trim()
        : undefined);

    if (!useContractDocumentTypeApi && !resolvedImageId) return;

    const document = yield call(fetchDocument, {
      documentId: String(documentId),
      documentImageId: useContractDocumentTypeApi ? undefined : resolvedImageId,
      source,
      avoidErrorMessage: true,
      useContractDocumentTypeApi,
    });

    if (!!document) return document;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'getDocumentFromUrlId',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* requestPdfDocument(
  action: PayloadAction<{
    documentId: string;
    source: ContractDocSource;
    documentImageId?: string;
    useContractDocumentTypeApi?: boolean;
  }>,
) {
  try {
    const document = yield call(fetchDocument, action.payload as Parameters<typeof fetchDocument>[0]);
    if (!!document) {
      yield put(pContractDocEditorActions.setPdfView(document));
      return true;
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'requestPdfDocument',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* fetchDocument({
  documentId,
  documentImageId,
  source,
  avoidErrorMessage,
  useContractDocumentTypeApi,
}: {
  documentId: string;
  documentImageId?: string;
  source: ContractDocSource;
  avoidErrorMessage?: boolean;
  useContractDocumentTypeApi?: boolean;
}) {
  try {
    const useClassicDocApi =
      useContractDocumentTypeApi === true || source === ContractDocSource.amieClassic;

    if (!useClassicDocApi && !documentImageId) {
      if (!avoidErrorMessage)
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('templateEditor.placement.snackbar.contractEditor.errorInFetchingDocument'),
            severity: 'error',
          }),
        );
      return null;
    }

    const classicNumericId = parseInt(String(documentId).trim(), 10);
    if (useClassicDocApi && (!Number.isFinite(classicNumericId) || classicNumericId <= 0)) {
      if (!avoidErrorMessage)
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('templateEditor.placement.snackbar.contractEditor.errorInFetchingDocument'),
            severity: 'error',
          }),
        );
      return null;
    }

    const sharedService: SharedService = yield getContext('sharedService');
    const response = useClassicDocApi
      ? yield call(TrackRequestPdfDoc, sharedService.getPlacementClassicDocument, 'Contract', classicNumericId)
      : yield call(TrackRequestPdfDoc, sharedService.getPlacementDocument, documentImageId ?? '');

    if (httpSuccess(response?.status))
      if (useClassicDocApi)
        return {
          ...response.data,
          data: response.data.data,
          documentId: response.data.id ?? String(classicNumericId),
        };
      else
        return {
          ...response.data,
          data: response.data.filebytes,
          documentId: response.data.documentId ?? documentId,
        };
    else {
      if (!avoidErrorMessage)
        yield put(
          globalActions.setSnackBar({
            message:
              httpClientError(response.status) && !!response.data
                ? response.data
                : i18next.t('templateEditor.placement.snackbar.contractEditor.errorInFetchingDocument'),
            severity: 'error',
          }),
        );
      return null;
    }
  } catch (error) {
    if (!avoidErrorMessage)
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.errorInFetchingDocument'),
          severity: 'error',
        }),
      );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'fetchDocument',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* placementCandidateContractSaga() {
  /** Business Saga - start */
  yield takeLatest(pContractDocEditorActions.onInitAction.type, onInit);
  yield takeLatest(pContractDocEditorActions.business_OnContractDocClick.type, onContractDocClick);
  yield takeLatest(
    pContractDocEditorActions.business_DownloadSelectedContractDoc.type,
    downloadSelectedCandidateContract,
  );
  /** Business Saga - end */

  /** Service saga - start */
  yield takeLatest(pContractDocEditorActions.getTemplates.type, requestTemplates);
  yield takeLatest(pContractDocEditorActions.getMergeTokensList.type, requestMergeTokenList);
  yield takeLatest(pContractDocEditorActions.generateDraft.type, generateDraft);
  yield takeLatest(pContractDocEditorActions.deleteContractDraft.type, deleteContractDraft);
  yield takeLatest(pContractDocEditorActions.deleteContractDraftNew.type, deleteContractDraftNew);
  yield takeLatest(pContractDocEditorActions.deleteContractDoc.type, deleteContractDoc);
  yield takeLatest(pContractDocEditorActions.previewContractDoc.type, previewContractDoc);
  yield takeLatest(pContractDocEditorActions.createContractDoc.type, createContractDoc);
  yield takeLatest(pContractDocEditorActions.publishContractDoc.type, publishContractDoc);
  yield takeLatest(pContractDocEditorActions.publishContractTscNew.type, publishContractTscNew);
  yield takeLatest(pContractDocEditorActions.voidContractDoc.type, voidContractDoc);
  yield takeLatest(pContractDocEditorActions.saveDraftTemplate.type, saveDraftTemplate);
  yield takeLatest(pContractDocEditorActions.getPdfDocument.type, requestPdfDocument);
  yield takeLatest(pContractDocEditorActions.updateContractStatus.type, updateContractStatusSaga);
  yield takeLatest(pContractDocEditorActions.getContractCatalogTemplates.type, requestContractCatalogTemplates);
  yield takeLatest(pContractDocEditorActions.fetchContractTemplateRows.type, requestContractTemplateRows);
  /** Service saga - end */
}
